import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Badge, Button, KeyValue, Section, Spinner } from '@zt/ui';
import { api } from '../lib/api';
import { HelpTip } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { useMoney } from '../lib/money';
import { useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { BizimhesapCompanyStatement } from '../lib/types';

export function useBizimhesapStatement(companyId: string, enabled = true) {
  return useQuery({
    queryKey: ['bizimhesap-statement', companyId],
    queryFn: () =>
      api.get<BizimhesapCompanyStatement>(`/v1/admin/companies/${companyId}/bizimhesap-statement`),
    enabled,
  });
}

/** "Bu bayiyi güncelle": ekstre BizimHesap'tan hemen çekilir. */
function RefreshButton({ companyId }: { companyId: string }) {
  const t = useT();
  const refresh = useApiMutation(
    () => api.post(`/v1/admin/companies/${companyId}/bizimhesap-statement/refresh`, {}),
    {
      invalidate: [['bizimhesap-statement', companyId], ['bizimhesap-diff'], ['company', companyId]],
      success: t('admin.bizimhesap.statement.refreshed'),
    },
  );
  return (
    <span className="inline-flex items-center gap-1">
      <Button size="sm" loading={refresh.isPending} onClick={() => refresh.mutate(undefined)}>
        {t('admin.bizimhesap.statement.refreshOne')}
      </Button>
      <HelpTip k="bizimhesap.refresh_one" />
    </span>
  );
}

/** Son güncelleme ve hata satırı (bayi kartı ve ekstre sayfası). */
function FreshnessLine({ s }: { s: NonNullable<BizimhesapCompanyStatement['statement']> }) {
  const t = useT();
  const { formatDate } = useI18n();
  return (
    <p className="text-sm text-ink-2">
      {s.fetchedAt
        ? t('admin.bizimhesap.statement.fetchedAt', { date: formatDate(s.fetchedAt, true) })
        : t('admin.bizimhesap.statement.neverFetched')}
      {s.error && (
        <span role="alert" className="block text-rose">
          {t('admin.bizimhesap.statement.lastError', { error: s.error })}
        </span>
      )}
    </p>
  );
}

/** Bayi kartında kısa özet: BizimHesap bakiyesi, son güncelleme ve "Bu bayiyi güncelle". */
export function BizimhesapBalanceSummary({ companyId }: { companyId: string }) {
  const t = useT();
  const money = useMoney();
  const { can } = usePermissions();
  const q = useBizimhesapStatement(companyId, can('b2b.ledger.read'));
  const s = q.data?.statement;
  if (!can('b2b.ledger.read') || !q.data?.linked) return null;
  return (
    <div className="mt-3 space-y-2 border-t border-line pt-3">
      {s && s.balanceMinor !== null && (
        <KeyValue
          items={[
            { label: t('admin.bizimhesap.statement.balance'), value: money(s.balanceMinor, s.currency) },
            { label: t('admin.bizimhesap.statement.pending'), value: money(s.pendingMinor, s.currency) },
          ]}
        />
      )}
      {s && <FreshnessLine s={s} />}
      <RefreshButton companyId={companyId} />
    </div>
  );
}

/**
 * Bayi ekstresi sayfasında BizimHesap ekstresi (F7-25, ADR-113): cari hesabın ana kaydı BizimHesap'tır; bakiye
 * ekstrenin son yürüyen bakiyesidir. Kredi limiti bu bakiye + BizimHesap'a henüz aktarılmamış açık hesap
 * siparişleriyle denetlenir. Fatura bağlantısı gelmeyen satır yalnızca bilgi olarak gösterilir.
 */
export function BizimhesapStatementSection({ companyId }: { companyId: string }) {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const q = useBizimhesapStatement(companyId);
  if (q.isLoading) return <Spinner label={t('common.loading')} />;
  if (!q.data) return null;
  const s = q.data.statement;
  return (
    <Section
      title={t('admin.bizimhesap.statement.title')}
      description={t('admin.bizimhesap.statement.lead')}
      action={<HelpTip k="bizimhesap.statement" />}
    >
      {!q.data.linked ? (
        <p className="text-sm text-ink-2">
          {t('admin.bizimhesap.statement.notLinked')}{' '}
          <Link to="/bizimhesap" className="text-pine underline underline-offset-2">
            {t('admin.nav.bizimhesap')}
          </Link>
        </p>
      ) : (
        <div className="space-y-3">
          {s && s.balanceMinor !== null && (
            <dl className="grid gap-3 sm:grid-cols-3" aria-label={t('admin.bizimhesap.statement.title')}>
              <div className="rounded-md border border-line bg-surface p-3">
                <dt className="text-sm text-ink-3">{t('admin.bizimhesap.statement.balance')}</dt>
                <dd className="mt-1 text-lg font-semibold tabular">{money(s.balanceMinor, s.currency)}</dd>
              </div>
              <div className="rounded-md border border-line bg-surface p-3">
                <dt className="text-sm text-ink-3">{t('admin.bizimhesap.statement.pending')}</dt>
                <dd className="mt-1 text-lg font-semibold tabular">{money(s.pendingMinor, s.currency)}</dd>
              </div>
              <div className="rounded-md border border-line bg-surface p-3">
                <dt className="text-sm text-ink-3">{t('admin.bizimhesap.statement.effective')}</dt>
                <dd className="mt-1 text-lg font-semibold tabular">{money(s.effectiveMinor, s.currency)}</dd>
              </div>
            </dl>
          )}
          <div className="flex flex-wrap items-center gap-3">
            {s ? (
              <FreshnessLine s={s} />
            ) : (
              <p className="text-sm text-ink-2">{t('admin.bizimhesap.statement.neverFetched')}</p>
            )}
            <RefreshButton companyId={companyId} />
          </div>
          {s && s.pendingOrders.length > 0 && (
            <p className="text-sm text-ink-2">
              {t('admin.bizimhesap.statement.pendingOrders', {
                orders: s.pendingOrders.map((o) => o.number).join(', '),
              })}
            </p>
          )}
          {s && s.rows.length > 0 && (
            <div className="overflow-x-auto rounded-md border border-line">
              <table
                className="w-full min-w-[40rem] text-sm"
                aria-label={t('admin.bizimhesap.statement.rows')}
              >
                <thead className="bg-surface-2 text-ink-2">
                  <tr>
                    <th className="px-3 py-2 text-start font-medium">
                      {t('admin.bizimhesap.statement.date')}
                    </th>
                    <th className="px-3 py-2 text-start font-medium">
                      {t('admin.bizimhesap.statement.type')}
                    </th>
                    <th className="px-3 py-2 text-start font-medium">
                      {t('admin.bizimhesap.statement.note')}
                    </th>
                    <th className="px-3 py-2 text-end font-medium">
                      {t('admin.bizimhesap.statement.debit')}
                    </th>
                    <th className="px-3 py-2 text-end font-medium">
                      {t('admin.bizimhesap.statement.credit')}
                    </th>
                    <th className="px-3 py-2 text-end font-medium">
                      {t('admin.bizimhesap.statement.running')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[...s.rows].reverse().map((r, i) => (
                    <tr key={i} className="border-t border-line">
                      <td className="px-3 py-2 whitespace-nowrap">
                        {r.date && /^\d{4}-\d{2}-\d{2}$/.test(r.date)
                          ? formatDate(r.date, false)
                          : (r.date ?? '—')}
                      </td>
                      <td className="px-3 py-2">
                        {r.link ? (
                          <a
                            href={r.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-pine hover:underline"
                          >
                            {r.type ?? '—'}
                          </a>
                        ) : (
                          (r.type ?? '—')
                        )}
                      </td>
                      <td className="px-3 py-2 text-ink-2">{r.note ?? ''}</td>
                      <td className="px-3 py-2 text-end tabular">
                        {r.debitMinor ? money(r.debitMinor, s.currency) : ''}
                      </td>
                      <td className="px-3 py-2 text-end tabular">
                        {r.creditMinor ? money(r.creditMinor, s.currency) : ''}
                      </td>
                      <td className="px-3 py-2 text-end tabular">
                        {r.balanceMinor === null ? '' : money(r.balanceMinor, s.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {s && s.balanceSource && s.balanceSource !== 'last_row' && (
            <Badge tone="amber">
              {t(`admin.bizimhesap.statement.sources.${s.balanceSource as 'field' | 'sums' | 'rows'}`)}
            </Badge>
          )}
        </div>
      )}
    </Section>
  );
}
